import { useEffect, useRef } from 'react';

import { useAuthUser } from 'auth/hooks';
import { Loader } from 'core/elements';
import { AlternateVersionHelper } from 'core/helpers';
import {
  CustomerQuoteUpsert,
  IQuickQuoteContactPreload,
  mapCreateCustomerQuoteForm,
} from 'quotes/components';
import { QuoteEntityType } from 'quotes/enums';

enum MessageType {
  ERROR = 'Error',
  SUBMIT = 'Submit',
}

interface IProps {
  entityType?: QuoteEntityType;
  preloadContact?: IQuickQuoteContactPreload;
  enableProspectQuickQuote?: boolean;
}

export const AddCustomerQuote = ({
  entityType,
  preloadContact,
  enableProspectQuickQuote,
}: IProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const { user, isFetched: isFetchedAuthUser } = useAuthUser();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({
    user,
  });

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type !== MessageType.SUBMIT) {
        return;
      }

      try {
        const data = await formRef.current?.triggerFormSubmit();

        event.source?.postMessage(
          {
            type: MessageType.SUBMIT,
            payload: mapCreateCustomerQuoteForm(data, entityType, isAlternateVersionEnabled),
          },
          { targetOrigin: '*' }
        );
      } catch (error) {
        event.source?.postMessage({ type: MessageType.ERROR }, { targetOrigin: '*' });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [entityType, isAlternateVersionEnabled]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--custom-leftMenuWidth', '0px');
      document.documentElement.style.setProperty('--custom-drawerContentPadding', '0px');
    }
  }, []);

  if (!isFetchedAuthUser) {
    return <Loader fullHeight />;
  }

  return (
    <CustomerQuoteUpsert
      ref={formRef}
      entityType={entityType}
      isOpen
      withoutForm
      stickySxProps={{ header: { top: 0 } }}
      preloadContact={preloadContact}
      enableProspectQuickQuote={enableProspectQuickQuote}
    />
  );
};
